/**
 * Worksmart AI Export Service
 * 
 * Handles CSV and PDF export functionality for Worksmart AI usage data.
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { WorksmartAIMemberData } from '../types/worksmartTypes';
import { getAIAdoptionLevel } from './worksmartAIService';

export interface WorksmartAIExportOptions {
  dataMode: string;
  weekDate: string;
  sortBy: string;
}

/**
 * Export Worksmart AI data to CSV
 */
export function exportWorksmartAIToCSV(
  data: WorksmartAIMemberData[],
  options: WorksmartAIExportOptions
): void {
  console.log('📥 [WORKSMART-AI-EXPORT] Exporting to CSV:', {
    users: data.length,
    weekDate: options.weekDate
  });

  // CSV headers
  const headers = [
    'Rank',
    'Name',
    'Email',
    'XO User ID',
    'WS User ID',
    'AI Hours',
    'Total Hours',
    'AI Ratio (%)',
    'Adoption Level'
  ];

  // Build CSV rows
  const rows = data.map((user, index) => {
    const level = getAIAdoptionLevel(user.aiRatioPercent);
    return [
      (index + 1).toString(),
      user.name,
      user.email || 'N/A',
      user.xoUserId.toString(),
      user.wsUserId.toString(),
      user.aiHours.toFixed(1),
      user.totalHours.toFixed(1),
      user.aiRatioPercent.toFixed(2),
      level.label
    ];
  });

  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  const filename = `worksmart-ai-report_${options.weekDate}_${options.dataMode}.csv`;
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  console.log('✅ [WORKSMART-AI-EXPORT] CSV export completed:', filename);
}

/**
 * Export Worksmart AI data to PDF
 */
export function exportWorksmartAIToPDF(
  data: WorksmartAIMemberData[],
  options: WorksmartAIExportOptions
): void {
  console.log('📥 [WORKSMART-AI-EXPORT] Exporting to PDF:', {
    users: data.length,
    weekDate: options.weekDate
  });

  const doc = new jsPDF();
  
  // Title
  doc.setFontSize(18);
  doc.text('Worksmart AI Usage Report', 14, 20);
  
  // Subtitle with date range
  doc.setFontSize(11);
  const weekStart = new Date(options.weekDate);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  
  const dateRange = `Week: ${weekStart.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  })} - ${weekEnd.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  })}`;
  
  doc.text(dateRange, 14, 28);
  doc.text(`Data Mode: ${options.dataMode}`, 14, 34);
  doc.text(`Total Users: ${data.length}`, 14, 40);
  
  // Calculate summary statistics
  const avgAIRatio = data.length > 0 
    ? data.reduce((sum, user) => sum + user.aiRatioPercent, 0) / data.length 
    : 0;
  
  doc.text(`Average AI Ratio: ${avgAIRatio.toFixed(2)}%`, 14, 46);
  
  // Prepare table data
  const tableData = data.map((user, index) => {
    const level = getAIAdoptionLevel(user.aiRatioPercent);
    return [
      (index + 1).toString(),
      user.name,
      user.email || 'N/A',
      user.aiHours.toFixed(1) + 'h',
      user.totalHours.toFixed(1) + 'h',
      user.aiRatioPercent.toFixed(2) + '%',
      level.label
    ];
  });
  
  // Generate table with color coding
  autoTable(doc, {
    startY: 52,
    head: [['Rank', 'Name', 'Email', 'AI Hours', 'Total Hours', 'AI Ratio', 'Level']],
    body: tableData,
    theme: 'striped',
    headStyles: { 
      fillColor: [0, 123, 255],
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    styles: { 
      fontSize: 9,
      cellPadding: 3
    },
    columnStyles: {
      0: { cellWidth: 15 },  // Rank
      1: { cellWidth: 40 },  // Name
      2: { cellWidth: 45 },  // Email
      3: { cellWidth: 20 },  // AI Hours
      4: { cellWidth: 25 },  // Total Hours
      5: { cellWidth: 20 },  // AI Ratio
      6: { cellWidth: 30 }   // Level
    },
    didParseCell: (cellData) => {
      // Color code the AI Ratio cells based on value
      if (cellData.column.index === 5 && cellData.section === 'body') {
        const rowIndex = cellData.row.index;
        const user = data[rowIndex];
        if (user) {
          const aiRatioPercent = user.aiRatioPercent;
          const level = getAIAdoptionLevel(aiRatioPercent);
          
          // Apply subtle background color based on adoption level
          // Lighten the color by blending with white (85% white + 15% color)
          const [r, g, b] = hexToRgb(level.color);
          const lightR = Math.round(255 * 0.85 + r * 0.15);
          const lightG = Math.round(255 * 0.85 + g * 0.15);
          const lightB = Math.round(255 * 0.85 + b * 0.15);
          
          cellData.cell.styles.fillColor = [lightR, lightG, lightB];
        }
      }
    }
  });
  
  // Add footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.getWidth() / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
    doc.text(
      `Generated: ${new Date().toLocaleString()}`,
      14,
      doc.internal.pageSize.getHeight() - 10
    );
  }
  
  // Save PDF
  const filename = `worksmart-ai-report_${options.weekDate}_${options.dataMode}.pdf`;
  doc.save(filename);

  console.log('✅ [WORKSMART-AI-EXPORT] PDF export completed:', filename);
}

/**
 * Convert hex color to RGB
 */
function hexToRgb(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
      ]
    : [0, 0, 0];
}

export default {
  exportWorksmartAIToCSV,
  exportWorksmartAIToPDF
};

